/*
 * /auth/account (page)
 */
import { supabase } from "@emr/auth/client"
import { appConfig } from "@emr/config"
import {
	Button,
	Card,
	CardContent,
	CardHeader,
	CardTitle,
	CardDescription,
	cn,
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
	Divider,
	Input,
	Label,
	Page,
	Popover,
	PopoverContent,
	PopoverTrigger,
	toast,
} from "@emr/react-ui"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { useState, useEffect } from "react"
import { useTranslation } from "react-i18next"
import { Check, ChevronsUpDown } from "lucide-react"
import { trpc } from "../../trpc"
import { authPageLoaderAuthenticated } from "../../utils/auth.utils"

const ALL_TIMEZONES: string[] =
	typeof Intl !== "undefined" && "supportedValuesOf" in Intl
		? (Intl as any).supportedValuesOf("timeZone")
		: []

function TimezoneSelect({
	value,
	onChange,
}: {
	value: string
	onChange: (tz: string) => void
}) {
	const [open, setOpen] = useState(false)

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild>
				<Button
					type={"button"}
					variant={"outline"}
					role={"combobox"}
					className={"w-full justify-between font-normal"}
				>
					<span className={"truncate"}>{value || "Select timezone…"}</span>
					<ChevronsUpDown className={"ml-2 size-4 shrink-0 opacity-50"} />
				</Button>
			</PopoverTrigger>
			<PopoverContent
				className={"w-[var(--radix-popover-trigger-width)] p-0"}
				align={"start"}
			>
				<Command>
					<CommandInput placeholder={"Search timezone…"} />
					<CommandList className={"max-h-52"}>
						<CommandEmpty>No timezone found.</CommandEmpty>
						<CommandGroup>
							{ALL_TIMEZONES.map((tz) => (
								<CommandItem
									key={tz}
									value={tz}
									onSelect={() => {
										onChange(tz)
										setOpen(false)
									}}
								>
									<Check
										className={cn(
											"mr-2 size-4",
											value === tz ? "opacity-100" : "opacity-0",
										)}
									/>
									{tz}
								</CommandItem>
							))}
						</CommandGroup>
					</CommandList>
				</Command>
			</PopoverContent>
		</Popover>
	)
}

export const Route = createFileRoute("/auth/account")({
	component: AccountPage,
	loader: authPageLoaderAuthenticated(),
})

function AccountPage() {
	const {
		user: { id, email },
	} = Route.useLoaderData()
	const { t } = useTranslation()
	const navigate = useNavigate()

	const { data: user, isLoading } = trpc.user.getByFilter.useQuery({
		where: { id },
	})

	const utils = trpc.useUtils()
	const updateUser = trpc.user.update.useMutation({
		onSuccess: () => {
			utils.user.getByFilter.invalidate()
			toast.success(t("auth.updateSuccess"))
		},
		onError: (err) => {
			toast.error(err.message)
		},
	})

	const [preferredName, setPreferredName] = useState("")
	const [avatarUrl, setAvatarUrl] = useState("")
	const [timezone, setTimezone] = useState(
		Intl.DateTimeFormat().resolvedOptions().timeZone,
	)

	useEffect(() => {
		if (user) {
			setPreferredName(user.preferredName ?? "")
			setAvatarUrl(user.avatarUrl ?? "")
			setTimezone(
				user.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
			)
		}
	}, [user])

	function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
		e.preventDefault()
		updateUser.mutate({
			id,
			preferredName,
			avatarUrl: avatarUrl || null,
			timezone: timezone || null,
		})
	}

	async function handleSignOut() {
		await supabase.auth.signOut()
		navigate({ to: appConfig.redirects.afterSignOutUrl })
	}

	const saving = updateUser.isPending

	return (
		<Page className={"bg-primary/30"}>
			<Card className={"w-full max-w-md p-8 pb-12"}>
				<CardHeader className={"items-center gap-2 pb-6"}>
					<CardTitle>{t("auth.account")}</CardTitle>
					<CardDescription>{email}</CardDescription>
				</CardHeader>
				<CardContent>
					<form onSubmit={handleSubmit} className={"flex flex-col gap-6"}>
						<div className={"grid gap-2"}>
							<Label htmlFor={"preferred-name"}>{t("auth.name")}</Label>
							<Input
								id={"preferred-name"}
								type={"text"}
								placeholder={"John Doe"}
								value={preferredName}
								onChange={(e) => setPreferredName(e.target.value)}
								disabled={isLoading}
								required
							/>
						</div>
						<div className={"grid gap-2"}>
							<Label htmlFor={"avatar-url"}>{t("auth.avatarUrl")}</Label>
							<Input
								id={"avatar-url"}
								type={"url"}
								placeholder={"https://example.com/avatar.png"}
								value={avatarUrl}
								onChange={(e) => setAvatarUrl(e.target.value)}
								disabled={isLoading}
							/>
						</div>
						<div className={"grid gap-2"}>
							<Label>Timezone</Label>
							<TimezoneSelect value={timezone} onChange={setTimezone} />
						</div>
						<Button
							type={"submit"}
							className={"w-full"}
							disabled={isLoading || saving}
						>
							{saving ? t("auth.saving") : t("auth.update")}
						</Button>
						<Divider />
						<Button
							type={"button"}
							variant={"outline"}
							className={"w-full"}
							onClick={handleSignOut}
						>
							{t("auth.signOut")}
						</Button>
					</form>
				</CardContent>
			</Card>
		</Page>
	)
}
